import {
  RecordResponse,
  RecordType,
  ResultType,
  TTournamentParams,
  Warrior,
} from '~/../api/common.types';
import Button from '~/components/common/Button';
import { TOURNAMENTS } from '~/constants';
import { useGameContext } from '~/contextProviders/GameContextProvider';

const EmptyRecord = ({ showRecord, setShowRecord, isOwner }:
  { showRecord: boolean; setShowRecord: (showRecord: boolean) => void; isOwner: boolean; }) => {
  return (
    <div className={`battle-record__wrapper${showRecord ? '--active' : ''}`}>
      <table className="battle-record">
        <thead>
          <tr>
            <th className="battle-record__battle">History</th>
            <th className="battle-record__result">Win / Lose</th>
            <th className="battle-record__attack">Attack</th>
            <th className="battle-record__hp">HP</th>
            <th className="battle-record__attack--opponent">Vs</th>
            <th className="battle-record__hp--opponent">HP</th>
            <th className="battle-record__opponent">Opponent #</th>
            <th className="battle-record__opponent-syndicate">Syndicate</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="battle-record__battle">
                No previous matches to show
            </td>
            <td className="battle-record__result"></td>
            <td className="battle-record__attack">
            </td>
            <td className="battle-record__hp"></td>
            <td className="battle-record__attack--opponent">
            </td>
            <td className="battle-record__attack--hp">
            </td>
            <td className="battle-record__opponent"></td>
            <td className="battle-record__opponent-syndicate">
            </td>
          </tr>
        </tbody>
      </table>
      <div className="battle-record__footer">
        <Button
          label={isOwner ? 'Hide Your Record' : 'Hide Opponent Record'}
          className="button"
          onClick={() => {
            setShowRecord(false);
          }}
        />
      </div>
    </div>
  );
};

export function generateContextEntries(
  warrior: Warrior,
  tournamentParam: TTournamentParams,
  reverse = true,
): RecordResponse | [] {
  const entries: any = [];
  const tournamentType = TOURNAMENTS[tournamentParam];
  const isActiveTournament = tournamentType?.isActive;
  const contextId = tournamentType?.apiContext;

  const contextChronicle = warrior.warrior_json.context_chronicles[contextId];
  // first round is happening
  if (isActiveTournament && !contextChronicle ) {
    const byeEntry = {
      label: 'Round 1',
      title: `${tournamentType.name} Round 1`,
      battleResult: 'Bye',
      iconImage: tournamentType.id,
      state: 0,
      warrior: {
        attack: 'transparent',
        isDefault: false,
        hp: undefined,
      },
      opponent: {
        attack: 'transparent',
        isDefault: false,
        hp: undefined,
        id: undefined,
        syndicate: undefined,
      },
    };
    entries.push(byeEntry);
    return entries;
  }

  if (!contextChronicle) {
    return [];
  }

  const contextChronicleSorted = contextChronicle.sort(
    (a, b) => a.state > b.state
  );
  // eslint-disable-next-line no-negated-condition
  const hadABye = contextChronicleSorted[0].state !== 0 ? true : false;

  // handle bye
  if (hadABye) {
    const entry = {
      label: 'Round 1',
      title: `${tournamentType.name} Round 1`,
      battleResult: 'Bye',
      iconImage: tournamentType.id,
      state: 0,
      warrior: {
        attack: 'transparent',
        isDefault: false,
        hp: undefined,
      },
      opponent: {
        attack: 'transparent',
        isDefault: false,
        hp: undefined,
        id: undefined,
        syndicate: undefined,
      },
    };
    entries.push(entry);
  }

  contextChronicleSorted.map((event: ResultType) => {
    const warriorAttack =
      event.entries.find((entry) => entry.warrior.id === warrior.warrior_id)
        ?.current_attack ||
      event.entries.find((entry) => entry.warrior.id === warrior.warrior_id)
        ?.warrior.syndicate;
    const warriorAttackIsDefault =
      event.entries.find((entry) => entry.warrior.id === warrior.warrior_id)
        ?.current_attack === null;

    const opponentAttack =
      event.entries.find((entry) => entry.warrior.id !== warrior.warrior_id)
        ?.current_attack ||
      event.entries.find((entry) => entry.warrior.id !== warrior.warrior_id)
        ?.warrior.syndicate;
    const isOpponentDefault =
      event.entries.find((entry) => entry.warrior.id !== warrior.warrior_id)
        ?.current_attack === null;

    let battleResult = '';
    if (event.victor === undefined) {
      battleResult = 'Tie';
    } else if (
      event.victor !== undefined &&
      event.victor === warrior.warrior_id
    ) {
      battleResult = 'Victory';
    } else if (
      event.victor !== undefined &&
      event.victor !== warrior.warrior_id
    ) {
      battleResult = 'Defeat';
    }

    const warriorRecord = event.entries.find(
      (entry) => entry.warrior.id === warrior.warrior_id
    )?.warrior;

    const opponent = event.entries.find(
      (entry) => entry.warrior.id !== warrior.warrior_id
    )?.warrior;
    const entry = {
      label: `Round ${event.state + 1}`,
      title: `${tournamentType.name} Round ${event.state + 1}`,
      battleResult: battleResult,
      state: event.state,
      iconImage: tournamentType.id,
      warrior: {
        attack: warriorAttack,
        isDefault: warriorAttackIsDefault,
        hp: warriorRecord?.hp,
      },
      opponent: {
        attack: opponentAttack,
        isDefault: isOpponentDefault,
        hp: opponent?.hp,
        id: opponent?.id,
        syndicate: opponent?.syndicate,
      },
    };
    entries.push(entry);
  });
  const sortedByState = entries.sort((a, b) => a.state - b.state);
  if (reverse) {
    return sortedByState.reverse();
  }
  return sortedByState;
}

export default function BattleRecord({ warrior, isOwner, showRecord, setShowRecord }: { warrior: Warrior, isOwner: boolean, showRecord: boolean, setShowRecord: (boolean)=> void }) {
  const { tournamentParam, totalRounds } = useGameContext();

  const isPeaceful = tournamentParam === 'peaceful';
  if (!showRecord) {
    return (
      <Button
        label={isOwner ? 'Your Battle Record' : 'Opponent Battle Record'}
        className="button battle-record__footer__button"
        onClick={() => {
          setShowRecord(true);
        }}
      />
    );
  }
  let currentContextResults: RecordResponse = [];
  // no current tournament record if first round
  if (totalRounds > 1) {
    currentContextResults = generateContextEntries(warrior, tournamentParam, true);
  }
  const tournamentsArray = Object.values(TOURNAMENTS);
  const shownTournaments = tournamentsArray.filter((tournament) => tournament.isShown);
  const previousTournamentKey = tournamentsArray[shownTournaments.length - 2].id;
  const previousTournament = TOURNAMENTS[previousTournamentKey];
  const contextResultsPreviousTournament: RecordResponse = generateContextEntries(warrior, previousTournament.id, true);
  const combinedEntries: RecordResponse = [...currentContextResults, ...contextResultsPreviousTournament];


  if (!combinedEntries.length && showRecord) {
    return <EmptyRecord showRecord={showRecord} setShowRecord={setShowRecord} isOwner={isOwner} />;
  }

  return (
    <div className={`battle-record__wrapper${showRecord ? '--active' : ''}`}>
      <BattleRecordTable combinedEntries={combinedEntries} isPeaceful={isPeaceful} />
      <div className="battle-record__footer">
        <Button
          label={isOwner ? 'Hide Your Record' : 'Hide Opponent Record'}
          className="button"
          onClick={() => {
            setShowRecord(false);
          }}
        />
      </div>
    </div>
  );
}

interface BattleRecordTableProps {
  combinedEntries: RecordType[];
  isPeaceful: boolean;
}

export const BattleRecordTable: React.FC<BattleRecordTableProps> = ({ combinedEntries, isPeaceful }) => {
  return (
    <table className="battle-record">
      <thead>
        <tr>
          <th className="battle-record__battle">History</th>
          <th className="battle-record__result">Result</th>
          <th className="battle-record__attack">Attack</th>
          <th className="battle-record__hp">HP</th>
          <th className="battle-record__attack--opponent">Vs</th>
          <th className="battle-record__hp--opponent">HP</th>
          <th className="battle-record__opponent">Opponent #</th>
          <th className="battle-record__opponent-syndicate">Syndicate</th>
        </tr>
      </thead>
      <tbody>
        {combinedEntries.map((entry: RecordType, index) => (
          <tr key={index}>
            <td className="battle-record__battle" title={entry.title}>
              <img
                src={`/img/bi-weekly/icon/${entry.iconImage}-icon.png`}
                alt="battle icon"
                className="battle-record__icon" /> {entry.label}
            </td>
            <td className="battle-record__result">{entry.battleResult}</td>
            <td className="battle-record__attack">
              <div>
                {entry.warrior.attack !== 'SPIRIT' && entry.warrior.attack !== 'RENEGADE' ? (
                  <img
                    src={
                      isPeaceful ?
                        `/img/peaceful/${entry.warrior.attack?.toLowerCase()}.png` :
                        `/img/syndicate.${entry.warrior.attack?.toLowerCase()}.png`
                    }
                    alt={entry.warrior.attack || 'attack image'}
                    title={
                      entry.warrior.isDefault ?
                        `Default ${entry.warrior.attack}` :
                        entry.warrior.attack
                    }
                    className="battle-record__attack__image"
                  />
                ) : (
                  'No Attack'
                )}
                {entry.warrior.isDefault && '(D)'}
              </div>
            </td>
            <td className="battle-record__hp">{entry.warrior.hp}</td>
            <td className="battle-record__attack--opponent">
              <div>
                {entry.opponent.attack !== 'SPIRIT' && entry.opponent.attack !== 'RENEGADE' ? (
                  <img
                    src={
                      isPeaceful ?
                        `/img/peaceful/${entry.opponent.attack?.toLowerCase()}.png` :
                        `/img/syndicate.${entry.opponent.attack?.toLowerCase()}.png`
                    }
                    alt={entry.opponent.attack || 'attack image'}
                    title={
                      entry.opponent.isDefault ?
                        `Default ${entry.opponent.attack}` :
                        entry.opponent.attack
                    }
                    className="battle-record__attack__image"
                  />
                ) : (
                  'No Attack'
                )}
                {entry.opponent.isDefault && '(D)'}
              </div>
            </td>
            <td className="battle-record__attack--hp">
              {entry.opponent.hp}
            </td>
            <td className="battle-record__opponent">{entry.opponent.id}</td>
            <td className="battle-record__opponent-syndicate">
              {entry.opponent.syndicate}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};
